import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { distinctUntilChanged, lastValueFrom, Observable, switchMap } from 'rxjs';
import { debounceTime, map, shareReplay, startWith, take, takeWhile } from 'rxjs/operators';
import { AnlagentypIconService } from '../../service/anlagentyp/anlagentyp-icon.service';
import { AnlagentypColorService } from '../../service/anlagentyp/anlagentyp-color.service';
import { AnlagentypService } from '../../service/anlagentyp/anlagentyp.service';
import { Anlagentyp } from '../../domain/model/anlagentyp';
import { NetzzuordnungService } from '../../service/netzzuordnung/netzzuordnung.service';
import { ErzeugungsanlageFilter } from '../../service/erzeugungsanlage/erzeugungsanlage-filter';
import { ErzeugungsanlageService } from '../../service/erzeugungsanlage/erzeugungsanlage.service';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../environments/environment';
import { NetzbetreiberService } from '../../service/netzbetreiber/netzbetreiber.service';
import { Netzbetreiber } from '../../domain/model/netzbetreiber';
import { ErzeugungsanlageExportService } from '../../service/erzeugungsanlage/erzeugungsanlage-export.service';
import { TnbUser } from '../../service/auth/tnb-user';
import { TeilgebietSelectionService } from '../../service/common/teilgebiet/teilgebiet-selection.service';

const FILTER_DEBOUNCE_TIME = 250;
const FILTER_OPTION_LIMIT = 50;

@Component({
    selector: 'tnb-erzeugungsanlage-filter',
    templateUrl: './erzeugungsanlage-filter.component.html',
    styleUrls: [
        './erzeugungsanlage-filter.component.scss',
    ],
    standalone: false,
})
export class ErzeugungsanlageFilterComponent implements OnInit, OnDestroy {
    anlagentypIconService = inject(AnlagentypIconService);
    anlagentypColorService = inject(AnlagentypColorService);
    private anlagentypService = inject(AnlagentypService);
    private netzbetreiberService = inject(NetzbetreiberService);
    private route = inject(ActivatedRoute);
    private netzzuordnungService = inject(NetzzuordnungService);
    private erzeugungsanlageService = inject(ErzeugungsanlageService);
    private erzeugungsanlageExportService = inject(ErzeugungsanlageExportService);
    private formBuilder = inject(FormBuilder);
    private teilgebietSelectionService = inject(TeilgebietSelectionService);
    private changeDetectorRef = inject(ChangeDetectorRef);

    public ASSET_URL = environment.ASSET_URL;

    stadtwerkeId?: string;

    @Input() initialFilter?: ErzeugungsanlageFilter;
    @Output() filter = new EventEmitter<ErzeugungsanlageFilter>();

    filterFormGroup: FormGroup;

    showWestnetzFilter = false;

    anlagentypen: Anlagentyp[] = [];

    public profile: TnbUser;

    filteredWestnetzId$: Observable<string[]>;
    filteredAnlagentype$: Observable<Anlagentyp[]>;
    filteredOrtsnamen$: Observable<string[]>;
    filteredStationsnamen$: Observable<string[]>;
    filteredNetzbetreiber$: Observable<string[]>;
    filteredNetzbetreiberTeilgebiet$: Observable<string[]>;

    private allNetzbetreiberForWestnetzView$: Observable<Netzbetreiber[]>;
    private componentDestroyed = false;

    async ngOnInit() {
        this.profile = this.route.snapshot.data.profile;
        if (this.profile.netzbetreiberType !== 'WESTNETZ') {
            this.stadtwerkeId = this.teilgebietSelectionService.getSelectedStadtwerkeId();
        } else {
            this.showWestnetzFilter = true;
        }
        this.allNetzbetreiberForWestnetzView$ =
            this.netzbetreiberService.getAlleNetzbetreiber()
                .pipe(
                    take(1),
                    shareReplay(1),
                );

        // Build form and set initial values
        this.filterFormGroup = this.formBuilder.group({
            westnetzId: [
                this.initialFilter.westnetzId,
            ],
            anlagentype: [
                this.initialFilter.anlagentype,
            ],
            stationsname: [
                this.initialFilter.stationsnamen,
            ],
            ortsname: [
                this.initialFilter.ortsnamen,
            ],
            installierteLeistungMin: [
                this.initialFilter.installierteLeistungMin,
            ],
            installierteLeistungMax: [
                this.initialFilter.installierteLeistungMax,
            ],
            netzbetreiber: [
                this.initialFilter.netzbetreiberName,
            ],
            netzbetreiberTeilgebiet: [
                this.initialFilter.netzbetreiberTeilgebiet,
            ],
            leitstellenRegion: [
                this.initialFilter.leitstellenRegion,
            ],
            status: [
                this.initialFilter.status,
            ],
        });

        this.filterFormGroup.valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.value),
                takeWhile(() => !this.componentDestroyed),
                map(val => {
                    return new ErzeugungsanlageFilter(
                        val.westnetzId,
                        this.stadtwerkeId,
                        val.installierteLeistungMin,
                        val.installierteLeistungMax,
                        val.anlagentype,
                        undefined,
                        val.ortsname,
                        val.stationsname,
                        undefined,
                        val.leitstellenRegion,
                        val.netzbetreiber,
                        val.netzbetreiberTeilgebiet,
                        val.status,
                    );
                }),
            )
            .subscribe(val => this.filter.next(val));

        this.filteredWestnetzId$ = this.filterFormGroup.get('westnetzId').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('westnetzId').value),
                distinctUntilChanged(),
                switchMap(westnetzId => this.erzeugungsanlageService.getWestnetzId(this.stadtwerkeId, westnetzId)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredOrtsnamen$ = this.filterFormGroup.get('ortsname').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('ortsname').value),
                distinctUntilChanged(),
                switchMap(ort => this.netzzuordnungService.getOrtsnamen(
                    this.stadtwerkeId,
                    ort,
                )),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredStationsnamen$ = this.filterFormGroup.get('stationsname').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('stationsname').value),
                distinctUntilChanged(),
                switchMap(stationsname => this.netzzuordnungService.getStationsnamen(
                    this.stadtwerkeId,
                    this.filterFormGroup.get('ortsname').value,
                    stationsname,
                )),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.anlagentypen = await lastValueFrom(this.anlagentypService.getAnlagentypen());
        this.filteredAnlagentype$ = this.filterFormGroup.get('anlagentype').valueChanges
            .pipe(
                startWith(this.filterFormGroup.get('anlagentype').value),
                distinctUntilChanged(),
                map(type => type ? this._filterAnlagentype(type) : this.anlagentypen.slice()),
            );

        this.filteredNetzbetreiber$ = this.filterFormGroup.get('netzbetreiber').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('netzbetreiber').value),
                map(name => name?.toLowerCase() || ''),
                distinctUntilChanged(),
                switchMap(name => {
                    return this.allNetzbetreiberForWestnetzView$.pipe(
                        map(allNetzbetreiber =>
                            name ? allNetzbetreiber.filter(n => n.name.toLowerCase().includes(name)) : allNetzbetreiber,
                        ),
                    );
                }),
                map(netzbetreiber => netzbetreiber.map(n => n.name)),
                map(options => {
                    return [
                        ...new Set(options),
                    ];
                }),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredNetzbetreiberTeilgebiet$ = this.filterFormGroup.get('netzbetreiberTeilgebiet').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('netzbetreiberTeilgebiet').value),
                map(teilgebiet => teilgebiet?.toLowerCase() || ''),
                distinctUntilChanged(),
                switchMap(teilgebiet => {
                    return this.allNetzbetreiberForWestnetzView$.pipe(
                        map(allNetzbetreiber =>
                            teilgebiet ? allNetzbetreiber.filter(n => n.teilgebiet.toLowerCase().includes(teilgebiet)) : allNetzbetreiber,
                        ),
                    );
                }),
                map(netzbetreiber => netzbetreiber.map(n => n.teilgebiet)),
                map(options => {
                    return [
                        ...new Set(options),
                    ];
                }),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );


        const minMax = await lastValueFrom(this.erzeugungsanlageService.getInstallierteLeistungRange(this.stadtwerkeId));
        const minControl = this.filterFormGroup.get('installierteLeistungMin');
        minControl.setValue(minControl.value ?? minMax.min);
        const maxControl = this.filterFormGroup.get('installierteLeistungMax');
        maxControl.setValue(maxControl.value ?? minMax.max);
        this.changeDetectorRef.markForCheck();
    }

    ngOnDestroy(): void {
        this.componentDestroyed = true;
    }

    private _filterAnlagentype(_value: string): Anlagentyp[] {
        return this.anlagentypen; //Primärenergieträger
    }

    public isWetznetzUser() {
        return this.profile.netzbetreiberType === 'WESTNETZ';
    }

    public exportErzeugungsanlagenCSV() {
        this.erzeugungsanlageExportService.exportErzeugungsanlagenCSV(
            this.teilgebietSelectionService.getSelectedTeilgebiet(),
        );
    }
}


